import { useRef } from 'react';

import { Plus, Layers, Hash } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { ReviewFieldEditor, type ReviewFieldDefinition } from './ReviewFieldEditor';

export type { ReviewFieldDefinition };

interface ReviewFieldsSchemaEditorProps {
  value: ReviewFieldDefinition[];
  onChange: (fields: ReviewFieldDefinition[]) => void;
  requiredFields: string[];
  onRequiredFieldsChange: (fields: string[]) => void;
  highIntentEmails?: string[];
  onHighIntentEmailsChange?: (emails: string[]) => void;
}

export const ReviewFieldsSchemaEditor = ({
  value: fields,
  onChange,
  requiredFields,
  onRequiredFieldsChange,
  highIntentEmails,
  onHighIntentEmailsChange,
}: ReviewFieldsSchemaEditorProps) => {
  const bottomRef = useRef<HTMLButtonElement>(null);

  const addField = (scrollToBottom = false) => {
    onChange([
      ...fields,
      {
        name: '',
        type: 'string',
        description: '',
        required: false,
        nullable: false,
      },
    ]);
    if (scrollToBottom) {
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  };

  const removeField = (index: number) => {
    const removedFieldName = fields[index].name;
    onChange(fields.filter((_, i) => i !== index));
    onRequiredFieldsChange(requiredFields.filter((name) => name !== removedFieldName));
  };

  const updateField = (index: number, updated: ReviewFieldDefinition) => {
    const newFields = [...fields];
    const oldName = newFields[index].name;
    const wasRequired = newFields[index].required;
    newFields[index] = updated;
    onChange(newFields);

    let newRequired = [...requiredFields];

    if (oldName && oldName !== updated.name) {
      newRequired = newRequired.map((name) => (name === oldName ? updated.name : name));
    }

    const fieldName = updated.name;
    if (fieldName) {
      if (updated.required && !newRequired.includes(fieldName)) {
        newRequired.push(fieldName);
      } else if (!updated.required && wasRequired) {
        newRequired = newRequired.filter((name) => name !== fieldName);
      }
    }

    onRequiredFieldsChange(newRequired);
  };

  return (
    <div className="space-y-4" role="region" aria-label="Response fields schema editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {fields.length > 0 && (
          <span className="text-muted-foreground flex items-center gap-1 text-xs">
            <Hash size={12} />
            {fields.length} {fields.length === 1 ? 'field' : 'fields'}
          </span>
        )}
        <Button
          type="button"
          onClick={() => addField(true)}
          size="sm"
          variant="outline"
          className="h-7"
        >
          <Plus size={14} className="mr-1" />
          Add Field
        </Button>
      </div>

      {fields.length > 0 ? (
        <div className="space-y-2">
          {fields.map((field, idx) => (
            <ReviewFieldEditor
              key={idx}
              field={field}
              onChange={(updated) => updateField(idx, updated)}
              onRemove={() => removeField(idx)}
              index={idx}
              highIntentEmails={highIntentEmails}
              onHighIntentEmailsChange={onHighIntentEmailsChange}
            />
          ))}
          <Button
            ref={bottomRef}
            type="button"
            onClick={() => addField(false)}
            size="sm"
            variant="outline"
            className="h-7 w-full border-dashed"
          >
            <Plus size={14} className="mr-1" />
            Add Field
          </Button>
        </div>
      ) : (
        <div className="text-muted-foreground bg-muted/30 flex flex-col items-center gap-3 rounded-lg border border-dashed py-10 text-center">
          <Layers className="h-8 w-8 opacity-40" />
          <div>
            <p className="text-sm font-medium">No response fields defined</p>
            <p className="mt-1 text-xs">
              Click &quot;Add Field&quot; to define what the AI should extract.
            </p>
          </div>
          <p className="text-xs opacity-60">
            System fields (analysis, confidence_score, *_matches) are auto-injected.
          </p>
        </div>
      )}
    </div>
  );
};
